/**
 * Claims Dashboard — Schadensmeldungen & Nachforschungsaufträge
 * Hauptseite mit KPI-Cards, Status-Filter, Tabelle + Pagination.
 * @page /claims
 * @roles admin, manager
 * @i18n claims
 * @endpoints GET /api/claims, GET /api/claims/stats, GET /api/claims/carriers
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { PageHeader, KpiCard, Badge, Button } from '../../components/ui';
import {
  Plus, ShieldAlert, Search as SearchIcon, Clock, CheckCircle2,
  XCircle, AlertTriangle, Package, ChevronLeft, ChevronRight,
  ArrowUpDown, Filter, X, Trash2, Loader2, Mail, Link2,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(`${API}${path}`, {
    ...opts,
    credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || data.message || `HTTP ${r.status}`);
    return data;
  });
}

// ── Status config ──

const STATUS_CONFIG = {
  submitted:            { dot: 'bg-blue-500',    bg: 'bg-blue-500/10',    text: 'text-blue-600 dark:text-blue-400' },
  in_review:            { dot: 'bg-yellow-500',  bg: 'bg-yellow-500/10',  text: 'text-yellow-600 dark:text-yellow-400' },
  documents_missing:    { dot: 'bg-orange-500',  bg: 'bg-orange-500/10',  text: 'text-orange-600 dark:text-orange-400' },
  approved:             { dot: 'bg-cyan-500',    bg: 'bg-cyan-500/10',    text: 'text-cyan-600 dark:text-cyan-400' },
  at_carrier:           { dot: 'bg-primary-500',  bg: 'bg-primary-500/10',  text: 'text-primary-600 dark:text-primary-400' },
  waiting_for_carrier:  { dot: 'bg-gray-400',    bg: 'bg-gray-500/10',    text: 'text-[var(--text-secondary,#4B5563)] dark:text-[var(--text-tertiary,#6B7280)]' },
  carrier_inquiry:      { dot: 'bg-yellow-500',  bg: 'bg-yellow-500/10',  text: 'text-yellow-600 dark:text-yellow-400' },
  carrier_response:     { dot: 'bg-cyan-500',    bg: 'bg-cyan-500/10',    text: 'text-cyan-600 dark:text-cyan-400' },
  objection:            { dot: 'bg-orange-500',  bg: 'bg-orange-500/10',  text: 'text-orange-600 dark:text-orange-400' },
  resolved:             { dot: 'bg-emerald-500', bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400' },
  rejected:             { dot: 'bg-gray-400',    bg: 'bg-gray-500/10',    text: 'text-[var(--text-secondary,#4B5563)] dark:text-[var(--text-tertiary,#6B7280)]' },
};

function ClaimStatusBadge({ status, t }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.submitted;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-badge text-[11px] font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {t(`claims.status.${status}`) || status}
    </span>
  );
}

function ClaimTypeBadge({ type, t }) {
  if (type === 'damage') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-badge text-[11px] font-medium bg-red-500/10 text-red-600 dark:text-red-400">
        {t('claims.damage')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-badge text-[11px] font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400">
      {t('claims.investigation')}
    </span>
  );
}

// ── Status filter tabs ──

const STATUS_TABS = [
  { key: '', label: 'claims.dashboard.showAll' },
  { key: 'submitted', label: 'claims.status.submitted' },
  { key: 'in_review', label: 'claims.status.in_review' },
  { key: 'at_carrier', label: 'claims.status.at_carrier' },
  { key: 'resolved', label: 'claims.status.resolved' },
  { key: 'rejected', label: 'claims.status.rejected' },
];

const PAGE_SIZE = 20;

export default function ClaimsDashboard() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Data
  const [claims, setClaims] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [carrierFilter, setCarrierFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('DESC');
  const [onlyUnread, setOnlyUnread] = useState(false); // #1224 P5

  // #1224 P1 — Bulk-Delete + Bulk-Stage
  const [userRole, setUserRole] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkStatusBusy, setBulkStatusBusy] = useState(false);
  const [bulkStatusError, setBulkStatusError] = useState(null);
  const isAdmin = ['admin', 'administrator'].includes(userRole || '');

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const data = await api('/api/claims/stats');
      setStats(data);
    } catch (err) {
      console.error('Stats fetch error:', err);
    }
  }, []);

  // Fetch carriers (for filter dropdown)
  const fetchCarriers = useCallback(async () => {
    try {
      const data = await api('/api/claims/carriers');
      setCarriers(data);
    } catch (err) {
      console.error('Carriers fetch error:', err);
    }
  }, []);

  // Fetch claims list
  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE, sort, order });
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('claim_type', typeFilter);
      if (carrierFilter) params.set('carrier_id', carrierFilter);
      if (search) params.set('search', search);
      if (onlyUnread) params.set('only_unread', '1');

      const data = await api(`/api/claims?${params}`);
      setClaims(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Claims fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, sort, order, statusFilter, typeFilter, carrierFilter, search, onlyUnread]);

  useEffect(() => { fetchStats(); fetchCarriers(); }, []);
  useEffect(() => { fetchClaims(); }, [fetchClaims]);
  useEffect(() => { setPage(1); }, [statusFilter, typeFilter, carrierFilter, search, onlyUnread]);
  useEffect(() => { setSelectedIds(new Set()); }, [page, sort, order, statusFilter, typeFilter, carrierFilter, search, onlyUnread]);
  // #1224 P5 — Auto-Refresh alle 60s (Liste + Stats)
  useEffect(() => {
    const tick = setInterval(() => { fetchClaims(); fetchStats(); }, 60_000);
    return () => clearInterval(tick);
  }, [fetchClaims, fetchStats]);
  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setUserRole(d?.user?.rolle || d?.rolle || null))
      .catch(() => {});
  }, []);

  const toggleSelect = (id, ev) => {
    ev?.stopPropagation?.();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (prev.size === claims.length && claims.length > 0) return new Set();
      return new Set(claims.map(c => c.id));
    });
  };
  const allSelected = claims.length > 0 && selectedIds.size === claims.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const handleBulkStatus = async () => {
    if (!bulkStatus) return;
    setBulkStatusBusy(true);
    setBulkStatusError(null);
    try {
      const ids = Array.from(selectedIds);
      const res = await api('/api/claims/bulk-status', { method: 'POST', body: { ids, status: bulkStatus } });
      setSelectedIds(new Set());
      setBulkStatus('');
      await Promise.all([fetchClaims(), fetchStats()]);
      console.info(`#1224 bulk-status OK: ${res.updated_count}/${ids.length} → ${bulkStatus}`);
    } catch (err) {
      setBulkStatusError(err.message || 'Bulk-Status fehlgeschlagen');
    } finally {
      setBulkStatusBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const ids = Array.from(selectedIds);
      const res = await api('/api/claims/bulk-delete', { method: 'POST', body: { ids } });
      setSelectedIds(new Set());
      setConfirmOpen(false);
      await Promise.all([fetchClaims(), fetchStats()]);
      console.info(`#1224 bulk-delete OK: ${res.deleted_count} gelöscht, ${res.inbox_restored_count} Mails zurück in Unassigned`);
    } catch (err) {
      setDeleteError(err.message || 'Bulk-Delete fehlgeschlagen');
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const showFrom = total > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const showTo = Math.min(page * PAGE_SIZE, total);

  const handleSort = (col) => {
    if (sort === col) {
      setOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSort(col);
      setOrder('DESC');
    }
  };

  const openDamage = stats?.open_damage ?? 0;
  const openInvestigation = stats?.open_investigation ?? 0;

  return (
    <div className="p-6">
      <PageHeader
        title={t('claims.title')}
        subtitle={t('claims.subtitle')}
        actions={
          <Button icon={Plus} onClick={() => navigate('/claims/new')}>
            {t('claims.newClaim')}
          </Button>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label={t('claims.dashboard.openClaims')}
          value={stats?.open ?? '–'}
          icon={ShieldAlert}
          color="bg-primary-500/10 text-primary-600 dark:text-primary-400"
        />
        <KpiCard
          label={t('claims.dashboard.openDamage')}
          value={openDamage}
          icon={Package}
          color="bg-red-500/10 text-red-600 dark:text-red-400"
        />
        <KpiCard
          label={t('claims.dashboard.openInvestigation')}
          value={openInvestigation}
          icon={SearchIcon}
          color="bg-blue-500/10 text-blue-600 dark:text-blue-400"
        />
        <KpiCard
          label={t('claims.dashboard.avgProcessingTime')}
          value={stats?.avg_resolution_days != null ? `${stats.avg_resolution_days} ${t('claims.dashboard.days')}` : '–'}
          icon={Clock}
          color="bg-amber-500/10 text-amber-600 dark:text-amber-400"
        />
      </div>

      {/* Status Tabs */}
      <div className="flex items-center gap-1 mb-4 bg-surface-card border border-line-card rounded-card p-1 overflow-x-auto">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-3 py-1.5 text-[12px] font-medium rounded-lg whitespace-nowrap transition-colors ${
              statusFilter === tab.key
                ? 'bg-white dark:bg-[#2A2D3D] text-content shadow-sm'
                : 'text-content-secondary hover:text-content hover:bg-surface-hover'
            }`}
          >
            {t(tab.label)}
            {tab.key === '' && stats?.total != null && (
              <span className="ml-1.5 text-[10px] text-content-tertiary">{stats.total}</span>
            )}
            {tab.key && stats?.by_status?.[tab.key] != null && (
              <span className="ml-1.5 text-[10px] text-content-tertiary">{stats.by_status[tab.key]}</span>
            )}
          </button>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={`${t('claims.claimNumber')}, ${t('claims.trackingNumber')}...`}
            className="w-full pl-9 pr-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content placeholder:text-content-tertiary focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-content-tertiary hover:text-content">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Carrier dropdown */}
        <select
          value={carrierFilter}
          onChange={e => setCarrierFilter(e.target.value)}
          className="px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">{t('claims.dashboard.filterCarrier')}</option>
          {carriers.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        {/* Type dropdown */}
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">{t('claims.dashboard.filterType')}</option>
          <option value="damage">{t('claims.damage')}</option>
          <option value="investigation">{t('claims.investigation')}</option>
        </select>

        {/* #1224 P5 — Quick-Filter "Mit neuen Nachrichten" */}
        <button
          onClick={() => setOnlyUnread(v => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium rounded-button border transition-colors ${
            onlyUnread
              ? 'bg-primary-500/10 border-primary-500/40 text-primary-600 dark:text-primary-400'
              : 'bg-surface-card border-line text-content-secondary hover:text-content'
          }`}
          title={t('claims.dashboard.bulk.unreadFilter') || 'Mit neuen Nachrichten'}
        >
          <Mail className="w-3.5 h-3.5" />
          {t('claims.dashboard.bulk.unreadFilter') || 'Mit neuen Nachrichten'}
        </button>

        {/* Clear filters */}
        {(statusFilter || typeFilter || carrierFilter || search || onlyUnread) && (
          <button
            onClick={() => { setStatusFilter(''); setTypeFilter(''); setCarrierFilter(''); setSearch(''); setOnlyUnread(false); }}
            className="flex items-center gap-1 px-2 py-1.5 text-[12px] text-content-secondary hover:text-content"
          >
            <X className="w-3.5 h-3.5" />
            Filter
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-surface-card border border-line-card rounded-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-line">
                <th className="px-3 py-3 w-9">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleSelectAll}
                    aria-label={t('claims.dashboard.bulk.selectAll') || 'Alle auswählen'}
                    className="w-4 h-4 rounded border-line text-primary-600 focus:ring-primary-500 cursor-pointer"
                  />
                </th>
                {[
                  { key: 'claim_number', label: t('claims.claimNumber'), sortable: true },
                  { key: 'created_at', label: t('common.date') || 'Datum', sortable: true },
                  { key: null, label: t('claims.dashboard.filterCustomer') },
                  { key: null, label: t('claims.carrier') },
                  { key: null, label: t('claims.trackingNumber') },
                  { key: null, label: t('claims.claimType') },
                  { key: 'status', label: 'Status', sortable: true },
                  { key: null, label: t('claims.dashboard.columns.messages') || 'Nachrichten' },
                  { key: 'goods_value', label: t('claims.goodsValue'), sortable: true },
                  { key: null, label: t('claims.assignedTo') },
                ].map((col, i) => (
                  <th
                    key={i}
                    className={`px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-content-secondary ${col.sortable ? 'cursor-pointer select-none hover:text-content' : ''}`}
                    onClick={() => col.sortable && col.key && handleSort(col.key)}
                  >
                    <span className="flex items-center gap-1">
                      {col.label}
                      {col.sortable && sort === col.key && (
                        <ArrowUpDown className="w-3 h-3 text-primary-500" />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-line last:border-0">
                    <td className="px-3 py-3.5 w-9"><div className="h-4 w-4 bg-surface-inset rounded animate-pulse" /></td>
                    {Array.from({ length: 10 }).map((_, j) => (
                      <td key={j} className="px-4 py-3.5">
                        <div className="h-4 bg-surface-inset rounded animate-pulse" style={{ width: `${40 + Math.random() * 60}%` }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : claims.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-[13px] text-content-secondary">
                    <ShieldAlert className="w-8 h-8 mx-auto mb-2 text-content-tertiary" />
                    {t('claims.noClaims')}
                  </td>
                </tr>
              ) : (
                claims.map(claim => (
                  <tr
                    key={claim.id}
                    className={`border-b border-line last:border-0 hover:bg-surface-hover transition-colors cursor-pointer ${
                      selectedIds.has(claim.id)
                        ? 'bg-primary-500/5'
                        : (claim.unread_count > 0 ? 'bg-amber-500/5' : '')
                    }`}
                    onClick={() => navigate(`/claims/${claim.id}`)}
                  >
                    <td className="px-3 py-3.5 w-9" onClick={ev => ev.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(claim.id)}
                        onChange={ev => toggleSelect(claim.id, ev)}
                        aria-label={t('claims.dashboard.bulk.selectRow') || 'Zeile auswählen'}
                        className="w-4 h-4 rounded border-line text-primary-600 focus:ring-primary-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[13px] font-semibold text-primary-600 dark:text-primary-400">
                          {claim.claim_number}
                        </span>
                        {/* #1248 — Linked-Cases Pill (Link2 + Counter + Indigo-Dot) */}
                        {(claim.linked_cases_count || 0) > 0 && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/claims/${claim.id}?tab=linked-cases`); }}
                            title={(claim.linked_case_displays && claim.linked_case_displays.length)
                              ? (t('claims.links.tooltip') || 'Verknüpfte Case(s): {nums}').replace('{nums}', claim.linked_case_displays.map(n => `#${n}`).join(', '))
                              : (t('claims.links.tooltipPlural') || '{n} verknüpfte Case(s)').replace('{n}', String(claim.linked_cases_count))}
                            className="inline-flex items-center gap-0.5 text-[var(--text-tertiary,#6B7280)] hover:text-indigo-500 transition-colors"
                          >
                            <Link2 className="w-3.5 h-3.5" />
                            {claim.linked_cases_count > 1 && (
                              <span className="text-[11px] font-medium">{claim.linked_cases_count}</span>
                            )}
                            {claim.has_active_linked_cases && (
                              <span className="ml-0.5 w-2 h-2 rounded-full bg-indigo-500" title={t('claims.links.activity') || 'Aktivität seit letztem Besuch'} />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-[13px] text-content-secondary whitespace-nowrap">
                      {new Date(claim.created_at).toLocaleDateString('de-AT')}
                    </td>
                    <td className="px-4 py-3.5 text-[13px] text-content">
                      {claim.customer_name || '–'}
                    </td>
                    <td className="px-4 py-3.5 text-[13px] text-content">
                      {claim.carrier_name || claim.carrier_name_raw || '–'}
                    </td>
                    <td className="px-4 py-3.5 text-[13px] text-content font-mono">
                      {claim.tracking_number}
                    </td>
                    <td className="px-4 py-3.5">
                      <ClaimTypeBadge type={claim.claim_type} t={t} />
                    </td>
                    <td className="px-4 py-3.5">
                      <ClaimStatusBadge status={claim.status} t={t} />
                    </td>
                    <td className="px-4 py-3.5">
                      {claim.unread_count > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-[11px] font-medium bg-amber-500/10 text-amber-700 dark:text-amber-400">
                          <Mail className="w-3 h-3" />
                          {claim.unread_count} {t('claims.messages.unread') || 'Neu'}
                        </span>
                      ) : (
                        <span className="text-content-tertiary text-[11px]">–</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-[13px] text-content whitespace-nowrap">
                      {claim.goods_value != null ? `€${Number(claim.goods_value).toLocaleString('de-AT')}` : '–'}
                    </td>
                    <td className="px-4 py-3.5 text-[13px] text-content-secondary">
                      {claim.assigned_name || '–'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* #1224 P1 — Sticky Bulk-Action Bar */}
        {selectedIds.size > 0 && (
          <div className="sticky bottom-4 z-30 mx-4 mt-3 mb-3 rounded-card border border-line-card bg-surface-card shadow-lg flex items-center justify-between px-4 py-2.5 flex-wrap gap-2">
            <div className="flex items-center gap-2 text-[13px] text-content">
              <CheckCircle2 className="w-4 h-4 text-primary-600" />
              <span>
                <strong>{selectedIds.size}</strong>{' '}
                {t('claims.dashboard.bulk.selectedSuffix') || 'ausgewählt'}
              </span>
              {bulkStatusError && (
                <span className="ml-2 px-2 py-0.5 text-[11px] rounded-button bg-red-500/10 text-red-700 dark:text-red-400">{bulkStatusError}</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={bulkStatus}
                onChange={e => { setBulkStatus(e.target.value); setBulkStatusError(null); }}
                disabled={bulkStatusBusy}
                className="px-2 py-1.5 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-60"
              >
                <option value="">{t('claims.dashboard.bulk.statusChoose') || 'Status ändern…'}</option>
                {['submitted','in_review','documents_missing','approved','at_carrier','waiting_for_carrier','carrier_inquiry','carrier_response','objection','resolved','rejected'].map(s => (
                  <option key={s} value={s}>{t(`claims.status.${s}`) || s}</option>
                ))}
              </select>
              <button
                onClick={handleBulkStatus}
                disabled={!bulkStatus || bulkStatusBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-button bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkStatusBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                {t('claims.dashboard.bulk.applyStatus') || 'Anwenden'}
              </button>
              <span className="w-px h-5 bg-line mx-1" />
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-3 py-1.5 text-[12px] text-content-secondary hover:text-content"
              >
                {t('claims.dashboard.bulk.clear') || 'Aufheben'}
              </button>
              {isAdmin && (
                <button
                  onClick={() => { setDeleteError(null); setConfirmOpen(true); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-button bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {t('claims.dashboard.bulk.delete') || 'Löschen'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* #1224 P1 — Confirm-Modal (kein window.confirm) */}
        {confirmOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => !deleting && setConfirmOpen(false)}>
            <div className="bg-surface-card border border-line-card rounded-card max-w-md w-full mx-4 p-5 shadow-xl" onClick={ev => ev.stopPropagation()}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-[15px] font-semibold text-content">
                    {t('claims.dashboard.bulk.confirmTitle') || 'Claims unwiderruflich löschen?'}
                  </h3>
                  <p className="mt-1 text-[13px] text-content-secondary leading-relaxed">
                    {(t('claims.dashboard.bulk.confirmBody') ||
                      '{count} Claim(s) inklusive Dokumente, Nachrichten und Storage-Dateien werden gelöscht. Zugeordnete E-Mails wandern zurück in den Unassigned-Eingang.').replace('{count}', String(selectedIds.size))}
                  </p>
                </div>
              </div>
              {deleteError && (
                <div className="mb-3 px-3 py-2 rounded-button bg-red-500/10 text-[12px] text-red-700 dark:text-red-400">
                  {deleteError}
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setConfirmOpen(false)}
                  disabled={deleting}
                  className="px-3 py-1.5 text-[13px] rounded-button border border-line text-content hover:bg-surface-hover disabled:opacity-50"
                >
                  {t('common.cancel') || 'Abbrechen'}
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={deleting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-button bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
                >
                  {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  {t('claims.dashboard.bulk.confirmDelete') || 'Endgültig löschen'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-line">
            <span className="text-[12px] text-content-secondary">
              {t('claims.dashboard.showing')
                .replace('{from}', showFrom)
                .replace('{to}', showTo)
                .replace('{total}', total)}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-button text-content-secondary hover:text-content hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum;
                if (totalPages <= 7) {
                  pageNum = i + 1;
                } else if (page <= 4) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 3) {
                  pageNum = totalPages - 6 + i;
                } else {
                  pageNum = page - 3 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`min-w-[32px] h-8 px-2 text-[12px] font-medium rounded-button transition-colors ${
                      page === pageNum
                        ? 'bg-primary-600 text-white'
                        : 'text-content-secondary hover:bg-surface-hover hover:text-content'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1.5 rounded-button text-content-secondary hover:text-content hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
